import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/features", label: "Features" },
  { to: "/templates", label: "Templates" },
  { to: "/pricing", label: "Pricing" },
  { to: "/why-phone-first", label: "Why phone-first" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const MarketingNav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50">
      <div className="mx-auto max-w-6xl px-5">
        <div className="mt-3 flex items-center justify-between rounded-full border border-black/5 bg-white/80 px-4 py-2.5 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.05)]">
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-3 py-2 text-sm font-medium text-ho-muted hover:text-ho-ink transition-colors rounded-full"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(user ? "/app" : "/login")}
              data-testid="nav-cta-button"
              className="tap-scale hidden sm:inline-flex rounded-full bg-ho-ink text-white text-sm font-semibold px-4 py-2 hover:bg-black"
            >
              {user ? "Dashboard" : "Start building"}
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              className="md:hidden tap-scale grid place-items-center w-9 h-9 rounded-full bg-black/5"
              aria-label="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="md:hidden mx-auto max-w-6xl px-5"
          >
            <div className="mt-2 rounded-3xl border border-black/5 bg-white/95 backdrop-blur-xl p-2 shadow-lg">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="tap-scale block rounded-2xl px-4 py-3 text-[15px] font-medium hover:bg-black/5"
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); navigate(user ? "/app" : "/login"); }}
                data-testid="mobile-nav-cta"
                className="tap-scale mt-1 w-full rounded-2xl bg-ho-primary text-white px-4 py-3 text-[15px] font-semibold"
              >
                {user ? "Go to Dashboard" : "Start building free"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
